import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, ImagePlus } from 'lucide-react';
interface ImageCarouselProps {
  title?: string;
  subtitle?: string;
  slides?: number;
  background?: 'white' | 'lightGreen' | 'darkGreen';
  aspectRatio?: 'video' | 'square' | 'wide';
}
export function ImageCarousel({
  title,
  subtitle,
  slides = 5,
  background = 'white',
  aspectRatio = 'video'
}: ImageCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const bgClasses = {
    white: 'bg-white',
    lightGreen: 'bg-lightGreen',
    darkGreen: 'bg-darkGreen'
  };
  const aspectClasses = {
    video: 'aspect-video',
    square: 'aspect-square',
    wide: 'aspect-[16/7]'
  };
  const isDark = background === 'darkGreen';
  const next = () => {
    setDirection(1);
    setCurrentIndex(prev => (prev + 1) % slides);
  };
  const prev = () => {
    setDirection(-1);
    setCurrentIndex(prev => (prev - 1 + slides) % slides);
  };
  const goTo = (index: number) => {
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  };
  return <section className={`py-24 md:py-28 ${bgClasses[background]}`} data-id="element-137">
      <div className="container mx-auto px-4" data-id="element-138">
        {(title || subtitle) && <div className="text-center mb-12 md:mb-16 max-w-3xl mx-auto" data-id="element-139">
            {title && <h2 className={`text-3xl md:text-4xl font-black mb-4 font-montserrat uppercase ${isDark ? 'text-white' : 'text-darkGreen'}`} data-id="element-140">
                {title}
              </h2>}
            {subtitle && <p className={`text-lg ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-141">
                {subtitle}
              </p>}
          </div>}

        <div className="max-w-5xl mx-auto" data-id="element-142">
          {/* Main slide */}
          <div className={`relative ${aspectClasses[aspectRatio]} rounded-3xl overflow-hidden shadow-xl bg-gradient-to-br from-gray-100 to-gray-200 border border-gray-200`} data-id="element-143">
            <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-144">
              <motion.div key={currentIndex} custom={direction} initial={{
              opacity: 0,
              x: direction > 0 ? 80 : -80
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: direction > 0 ? -80 : 80
            }} transition={{
              duration: 0.4,
              ease: 'easeInOut'
            }} className="absolute inset-0 flex flex-col items-center justify-center text-gray-400" data-id="element-145">
                {/* Empty placeholder slide */}
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-white/70 backdrop-blur flex items-center justify-center mb-4 shadow-sm" data-id="element-146">
                  <ImagePlus className="w-10 h-10 md:w-12 md:h-12 text-vibrantGreen/70" data-id="element-147" />
                </div>
                <p className="font-bold text-darkGreen/60 text-base md:text-lg font-montserrat uppercase tracking-wider" data-id="element-148">
                  Imagen {currentIndex + 1} / {slides}
                </p>
                <p className="text-sm text-gray-500 mt-2 italic px-4 text-center" data-id="element-149">
                  Agrega aquí tu imagen
                </p>
              </motion.div>
            </AnimatePresence>

            {/* Prev / Next Buttons */}
            <button onClick={prev} aria-label="Imagen anterior" className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/90 hover:bg-white text-darkGreen shadow-md hover:shadow-lg transition-all flex items-center justify-center group" data-id="element-150">
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 group-hover:-translate-x-0.5 transition-transform" data-id="element-151" />
            </button>
            <button onClick={next} aria-label="Imagen siguiente" className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/90 hover:bg-white text-darkGreen shadow-md hover:shadow-lg transition-all flex items-center justify-center group" data-id="element-152">
              <ChevronRight className="w-5 h-5 md:w-6 md:h-6 group-hover:translate-x-0.5 transition-transform" data-id="element-153" />
            </button>
          </div>

          {/* Dots indicator */}
          <div className="flex items-center justify-center gap-2 mt-6" data-id="element-154">
            {Array.from({
            length: slides
          }).map((_, i) => <button key={i} onClick={() => goTo(i)} aria-label={`Ir a imagen ${i + 1}`} className={`h-2 rounded-full transition-all duration-300 ${i === currentIndex ? 'w-8 bg-vibrantGreen' : `w-2 ${isDark ? 'bg-white/30 hover:bg-white/50' : 'bg-gray-300 hover:bg-gray-400'}`}`} data-id="element-155" />)}
          </div>

          {/* Thumbnail strip (hidden on mobile for cleanliness) */}
          <div className="hidden md:grid grid-cols-5 gap-3 mt-6" data-id="element-156">
            {Array.from({
            length: slides
          }).map((_, i) => <button key={i} onClick={() => goTo(i)} className={`aspect-square rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 border-2 transition-all flex items-center justify-center ${i === currentIndex ? 'border-vibrantGreen shadow-md scale-105' : 'border-transparent opacity-60 hover:opacity-100'}`} aria-label={`Vista previa imagen ${i + 1}`} data-id="element-157">
                <ImagePlus className="w-5 h-5 text-vibrantGreen/60" data-id="element-158" />
              </button>)}
          </div>
        </div>
      </div>
    </section>;
}